import { Link, NavLink, useLocation } from 'react-router-dom';
import { Moon, Sun, Menu, X, Sparkles, Type, ChevronDown, ShieldCheck } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useI18n, SUPPORTED_LANGS } from '../lib/i18n';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useFontSize } from '../context/FontSizeContext';

function Logo() {
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
      <svg width="28" height="28" viewBox="0 0 32 32" fill="none" className="text-brand-deep dark:text-brand-gold-dark">
        <path d="M16 3L28 24H4L16 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M16 29L4 8H28L16 29Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" opacity="0.55" />
      </svg>
      <span className="font-display text-lg font-semibold tracking-tight">Tikkun</span>
    </Link>
  );
}

function LangSwitch() {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  const active = SUPPORTED_LANGS.find((l) => l.key === lang) || SUPPORTED_LANGS[0];
  return (
    <div className="relative" ref={ref} data-testid="lang-switch">
      <button onClick={() => setOpen(!open)} data-testid="lang-switch-btn" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-border text-xs font-medium hover:bg-secondary transition-colors">
        {active.label} <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 z-50 min-w-[160px] rounded-2xl border border-border bg-popover shadow-xl shadow-black/10 p-1">
          {SUPPORTED_LANGS.map((l) => (
            <button
              key={l.key}
              onClick={() => { setLang(l.key); setOpen(false); }}
              data-testid={`lang-${l.key}`}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${lang === l.key ? 'bg-secondary font-medium' : 'hover:bg-secondary'}`}
            >
              <span className="text-muted-foreground text-xs mr-2 uppercase">{l.label}</span>{l.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme"
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-200">
      {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}

function FontSizeToggle() {
  const { size, setSize } = useFontSize();
  const order = ['sm', 'md', 'lg', 'xl'];
  const next = () => setSize(order[(order.indexOf(size) + 1) % order.length]);
  return (
    <button onClick={next} data-testid="font-size-toggle" aria-label="Font size"
      className="inline-flex h-9 items-center gap-1 px-2.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-200 text-xs font-medium">
      <Type size={14} /> {size.toUpperCase()}
    </button>
  );
}

export function Header() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const primaryLinks = [
    { to: '/manifesto', label: t.nav.manifesto, id: 'nav-manifesto' },
    { to: '/library', label: t.nav.library, id: 'nav-library' },
    { to: '/projects', label: t.nav.projects, id: 'nav-projects' },
    { to: '/help', label: t.nav.help, id: 'nav-help' },
  ];
  const secondaryLinks = [
    { to: '/collaborators', label: t.nav.collaborators, id: 'nav-collaborators' },
    { to: '/testimonials', label: t.nav.testimonials, id: 'nav-testimonials' },
    { to: '/transparency', label: t.nav.transparency, id: 'nav-transparency' },
    { to: '/assistant', label: t.nav.assistant, id: 'nav-assistant' },
    { to: '/contact', label: t.nav.contact, id: 'nav-contact' },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/75 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {primaryLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.id}
              className={({ isActive }) => `px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${isActive ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`}>
              {l.label}
            </NavLink>
          ))}
          <div className="relative" ref={moreRef}>
            <button onClick={() => setMoreOpen(!moreOpen)} data-testid="nav-more"
              className="px-3 py-1.5 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              Altro <ChevronDown size={12} />
            </button>
            {moreOpen && (
              <div className="absolute left-0 mt-2 z-50 min-w-[200px] rounded-2xl border border-border bg-popover shadow-xl shadow-black/10 p-1">
                {secondaryLinks.map((l) => (
                  <NavLink key={l.to} to={l.to} onClick={() => setMoreOpen(false)} data-testid={l.id}
                    className="block px-3 py-2 rounded-xl text-sm hover:bg-secondary">{l.label}</NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <FontSizeToggle />
          <LangSwitch />
          <ThemeToggle />
          {user ? (
            <>
              {user.role === 'admin' && (
                <Link to="/admin" data-testid="nav-admin" className="text-sm font-medium px-3 py-1.5 rounded-full text-brand-gold-dark inline-flex items-center gap-1">
                  <ShieldCheck size={14} /> {t.nav.admin}
                </Link>
              )}
              <Link to="/settings" data-testid="nav-settings" className="text-sm font-medium px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground">
                Impostazioni
              </Link>
              <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm font-medium px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground">
                {t.nav.dashboard}
              </Link>
              <button onClick={logout} data-testid="nav-logout" className="text-sm font-medium px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground">
                {t.nav.logout}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium px-3 py-1.5 text-muted-foreground hover:text-foreground">{t.nav.login}</Link>
              <Link to="/register" data-testid="nav-register" className="text-sm font-medium px-4 py-2 rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity">{t.nav.register}</Link>
            </>
          )}
        </div>
        <button className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-border" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl px-6 py-4 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          {[...primaryLinks, ...secondaryLinks].map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-sm">{l.label}</NavLink>
          ))}
          <div className="flex items-center gap-2 pt-2 flex-wrap">
            <FontSizeToggle /><LangSwitch /><ThemeToggle />
          </div>
          {user ? (
            <div className="flex gap-3 pt-3 flex-wrap">
              {user.role === 'admin' && <Link to="/admin" className="text-sm text-brand-gold-dark" onClick={() => setOpen(false)}>{t.nav.admin}</Link>}
              <Link to="/dashboard" className="text-sm" onClick={() => setOpen(false)}>{t.nav.dashboard}</Link>
              <button onClick={() => { logout(); setOpen(false); }} className="text-sm">{t.nav.logout}</button>
            </div>
          ) : (
            <div className="flex gap-3 pt-3">
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm">{t.nav.login}</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-medium">{t.nav.register}</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-border mt-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2 space-y-3">
          <Logo />
          <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">{t.footer.tagline}</p>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{t.footer.product}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/library" className="hover:text-foreground text-muted-foreground">{t.nav.library}</Link></li>
            <li><Link to="/projects" className="hover:text-foreground text-muted-foreground">{t.nav.projects}</Link></li>
            <li><Link to="/help" className="hover:text-foreground text-muted-foreground">{t.nav.help}</Link></li>
            <li><Link to="/assistant" className="hover:text-foreground text-muted-foreground">{t.nav.assistant}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{t.footer.company}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/manifesto" className="hover:text-foreground text-muted-foreground">{t.nav.manifesto}</Link></li>
            <li><Link to="/collaborators" className="hover:text-foreground text-muted-foreground">{t.nav.collaborators}</Link></li>
            <li><Link to="/testimonials" className="hover:text-foreground text-muted-foreground">{t.nav.testimonials}</Link></li>
            <li><Link to="/transparency" className="hover:text-foreground text-muted-foreground">{t.nav.transparency}</Link></li>
            <li><Link to="/contact" className="hover:text-foreground text-muted-foreground">{t.nav.contact}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{t.footer.legal}</h4>
          <ul className="space-y-2 text-sm">
            <li><span className="text-muted-foreground">Privacy</span></li>
            <li><span className="text-muted-foreground">Terms</span></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Tikkun. {t.footer.rights}</span>
          <span className="inline-flex items-center gap-1.5"><Sparkles size={12} /> v0.2 — Phase 2</span>
        </div>
      </div>
    </footer>
  );
}

export function PageLayout({ children }) {
  const location = useLocation();
  return (
    <div className="min-h-screen flex flex-col" key={location.pathname}>
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
